import { toPng } from "html-to-image";

// Screenshots for Help & feedback (docs/help-feedback-plan.md).
//
// 1. captureViewport(): renders the page DOM to a PNG without any browser prompt. WebGL canvases
//    come out blank unless they are redrawn at that instant (preserveDrawingBuffer is off by
//    design — see resultsviz/imageExport.js), so the 3D viewers register a synchronous redraw
//    with registerCanvasRenderer() and we call it right before the capture.
// 2. captureScreen(): the browser's own screen capture (pixel-perfect, one prompt), kept as the
//    fallback for anything the DOM render cannot show.
const canvasRenderers = new Map();

const withTimeout = (promise, ms, label) =>
  Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timed out`)), ms))]);


// `render` either redraws synchronously (three.js: renderer.render) and returns nothing — the
// canvas is read back right after — or returns a Promise of a data URL (Mapbox GL: read the
// canvas inside its own 'render' event, the documented way to screenshot a map without the
// permanent cost of preserveDrawingBuffer).
export const registerCanvasRenderer = (canvas, render) => {
  if (!canvas || typeof render !== "function") return () => {};
  canvasRenderers.set(canvas, render);
  return () => canvasRenderers.delete(canvas);
};
// For a cleanup that runs in another scope than the registration (GeometryView removes its map
// in a separate effect). A detached canvas is dropped lazily anyway, so this is tidiness.
export const unregisterCanvasRenderer = (canvas) => canvasRenderers.delete(canvas);

// html-to-image reads each <canvas> with toDataURL() while it CLONES the DOM, which is several
// awaits after we start — by then the next animation frame has cleared a WebGL canvas again.
// So each registered viewer is redrawn and read back synchronously HERE, and the pixels are
// placed over the canvas as a plain <img> for the duration of the capture (same rectangle, so
// nothing visibly changes), then removed.
const overlayCanvases = async () => {
  const overlays = [];
  for (const [canvas, render] of canvasRenderers) {
    if (!canvas.isConnected) {
      canvasRenderers.delete(canvas);
      continue;
    }
    try {
      const produced = render();
      // A synchronous redraw is read back in the same tick (a microtask away is still before the
      // next animation frame clears it); a promise brings its own pixels.
      const dataUrl =
        produced && typeof produced.then === "function"
          ? await withTimeout(produced, 3000, "canvas capture")
          : canvas.toDataURL("image/png");
      if (typeof dataUrl !== "string") continue;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) continue;
      const img = document.createElement("img");
      img.src = dataUrl;
      img.setAttribute("data-screenshot-overlay", "");
      Object.assign(img.style, {
        position: "fixed",
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        pointerEvents: "none",
        zIndex: "2147483000",
      });
      document.body.appendChild(img);
      overlays.push(img);
    } catch {
      /* a viewer that cannot be read just stays blank in the shot */
    }
  }
  return () => overlays.forEach((img) => img.remove());
};

// MUI hides an empty field's placeholder (opacity 0 on ::placeholder) until the field is focused,
// and html-to-image cannot see pseudo-element rules, so the placeholder was drawn over the label.
// Blank those placeholders for the instant of the capture; everything else keeps its placeholder.
const hideUnfocusedMuiPlaceholders = () => {
  const touched = [];
  for (const input of document.querySelectorAll(".MuiFormControl-root input[placeholder], .MuiFormControl-root textarea[placeholder]")) {
    if (input.value || input === document.activeElement) continue;
    const control = input.closest(".MuiFormControl-root");
    const label = control?.querySelector(".MuiInputLabel-root");
    if (label && !label.classList.contains("MuiInputLabel-shrink")) {
      touched.push([input, input.getAttribute("placeholder")]);
      input.setAttribute("placeholder", "");
    }
  }
  return () => touched.forEach(([input, ph]) => input.setAttribute("placeholder", ph));
};

export const MAX_SHOT_WIDTH = 1600;

const shrink = async (dataUrl) => {
  const img = new Image();
  img.src = dataUrl;
  await img.decode();
  if (img.width <= MAX_SHOT_WIDTH) return dataUrl;
  const scale = MAX_SHOT_WIDTH / img.width;
  const c = document.createElement("canvas");
  c.width = Math.round(img.width * scale);
  c.height = Math.round(img.height * scale);
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  return c.toDataURL("image/png");
};

// Elements marked data-no-screenshot (the feedback dialog, the launcher tab) are left out, and
// so is <noscript>: html-to-image renders its "You need to enable JavaScript" fallback as
// visible text at the top of the page, shifting everything below it.
const filter = (node) => {
  if (node?.dataset && "noScreenshot" in node.dataset) return false;
  if (node?.tagName === "NOSCRIPT") return false;
  return true;
};

// The page's own background, so areas the app leaves transparent are not grey in the PNG.
const pageBackground = () => {
  for (const el of [document.body, document.documentElement]) {
    const c = getComputedStyle(el).backgroundColor;
    if (c && c !== "transparent" && c !== "rgba(0, 0, 0, 0)") return c;
  }
  return "#ffffff";
};

export const captureViewport = async () => {
  const removeOverlays = await overlayCanvases();
  const restorePlaceholders = hideUnfocusedMuiPlaceholders();
  let dataUrl;
  try {
    dataUrl = await withTimeout(
    toPng(document.body, {
      filter,
      backgroundColor: pageBackground(),
      // Covers and other images are S3 signed links the page loaded as plain <img>; the cached
      // reply carries no CORS header, so a CORS re-fetch served from cache is refused and the
      // image comes out blank. no-store re-reads from S3 (same URL, signature still valid).
      fetchRequestInit: { cache: "no-store", mode: "cors", credentials: "omit" },
      pixelRatio: 1,
      cacheBust: false,
      skipFonts: true,
      // Cross-origin images (S3 cover photos) that refuse CORS become a grey box, not a failure.
      imagePlaceholder:
        "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxIiBoZWlnaHQ9IjEiPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiNlMmU4ZjAiLz48L3N2Zz4=",
      width: window.innerWidth,
      height: window.innerHeight,
      style: { transform: `translate(${-window.scrollX}px, ${-window.scrollY}px)` },
    }),
    10000,
    "page capture",
  );
  } finally {
    restorePlaceholders();
    removeOverlays();
  }
  return shrink(dataUrl);
};

export const canCaptureScreen = () =>
  typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getDisplayMedia);

export const captureScreen = async () => {
  const stream = await navigator.mediaDevices.getDisplayMedia({ video: { displaySurface: "browser" }, audio: false, preferCurrentTab: true });
  try {
    const track = stream.getVideoTracks()[0];
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    // One frame is enough; give the compositor a moment so the first frame is not black.
    await new Promise((r) => setTimeout(r, 250));
    const c = document.createElement("canvas");
    c.width = video.videoWidth;
    c.height = video.videoHeight;
    c.getContext("2d").drawImage(video, 0, 0);
    track.stop();
    return shrink(c.toDataURL("image/png"));
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
};

export const dataUrlToBlob = async (dataUrl) => (await fetch(dataUrl)).blob();

// The registration for a Mapbox GL map: pixels are read inside the map's own 'render' event.
export const mapboxCanvasRenderer = (map) => () =>
  new Promise((resolve, reject) => {
    try {
      map.once("render", () => {
        try {
          resolve(map.getCanvas().toDataURL("image/png"));
        } catch (e) {
          reject(e);
        }
      });
      map.triggerRepaint();
    } catch (e) {
      reject(e);
    }
  });
